import { useState, useEffect, useMemo } from 'react';
import { Edit2, Save, X, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getSectionVisibility,
  updateSectionVisibility,
  createSectionVisibility
} from 'wasp/client/operations';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import LoadingSpinner from '../layout/LoadingSpinner';

// Default sections from the dashboard config
const DEFAULT_SECTIONS = [
  { key: 'notices', label: 'Notices', category: 'Information' },
  { key: 'events', label: 'Events/Activities', category: 'Information' },
  { key: 'tenants', label: 'Tenants', category: 'Directory' },
  { key: 'food-court', label: 'Food Court', category: 'Directory' },
  { key: 'facilities', label: 'Facilities', category: 'Directory' },
  { key: 'services', label: 'Services', category: 'Directory' },
  { key: 'buy-sell', label: 'Buy & Sell', category: 'Community' },
  { key: 'request-offer', label: 'Request & Offer', category: 'Community' },
  { key: 'lost-found', label: 'Lost & Found', category: 'Community' },
  { key: 'transportation', label: 'Transportation', category: 'Community' },
  { key: 'housing', label: 'Housing', category: 'Community' }
];

// Location level options for the hierarchy selector
const LOCATION_LEVELS = [
  { value: 'country', label: 'Country' },
  { value: 'province', label: 'Province' },
  { value: 'city', label: 'City' },
  { value: 'neighbourhood', label: 'Neighbourhood' },
  { value: 'organization', label: 'Organization' },
] as const;

type LevelValue = typeof LOCATION_LEVELS[number]['value'];

function getLevelsLabel(levels: string[]): string {
  if (levels.length === 0 || levels.length === LOCATION_LEVELS.length) return 'All levels';
  if (levels.length === 1) {
    const match = LOCATION_LEVELS.find((l) => l.value === levels[0]);
    return match ? `${match.label} only` : levels[0];
  }
  return levels
    .map((v) => LOCATION_LEVELS.find((l) => l.value === v)?.label ?? v)
    .join(', ');
}

function LevelsSelector({
  value,
  onChange,
  disabled = false
}: {
  value: string[];
  onChange: (levels: string[]) => void;
  disabled?: boolean;
}) {
  const toggle = (level: LevelValue) => {
    const next = value.includes(level)
      ? value.filter((v) => v !== level)
      : [...value, level];
    onChange(next);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs px-2 min-w-[100px] max-w-full justify-between"
        >
          <span className="truncate">{getLevelsLabel(value)}</span>
          <ChevronDown className="h-3 w-3 ml-1 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Visible at levels</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LOCATION_LEVELS.map((level) => (
          <DropdownMenuCheckboxItem
            key={level.value}
            checked={value.includes(level.value)}
            onCheckedChange={() => toggle(level.value)}
          >
            {level.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground px-2 pb-1">
          Empty = all levels visible
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type SectionVisibility = {
  id: string;
  sectionKey: string;
  isVisible: boolean;
  isActive: boolean;
  customLabel: string | null;
  displayOrder: number;
  visibleAtLevels: string[];
};

type SortField = 'category' | 'section' | 'label' | 'visible' | 'active';
type SortDir = 'asc' | 'desc';

const SectionVisibilityTab = () => {
  const [sections, setSections] = useState<SectionVisibility[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const loadSections = async () => {
    try {
      setLoading(true);
      const data = await getSectionVisibility();

      const merged = DEFAULT_SECTIONS.map((defaultSection) => {
        const existing = data.find((s: any) => s.sectionKey === defaultSection.key);
        return (
          existing || {
            id: `temp-${defaultSection.key}`,
            sectionKey: defaultSection.key,
            isVisible: true,
            isActive: true,
            customLabel: null,
            displayOrder: 0,
            visibleAtLevels: []
          }
        );
      });

      setSections(merged);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load section visibility settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  const getDisplayLabel = (section: SectionVisibility) => {
    if (section.customLabel) return section.customLabel;
    const defaultSection = DEFAULT_SECTIONS.find((s) => s.key === section.sectionKey);
    return defaultSection?.label || section.sectionKey;
  };

  const getCategory = (sectionKey: string) => {
    const defaultSection = DEFAULT_SECTIONS.find((s) => s.key === sectionKey);
    return defaultSection?.category || 'Unknown';
  };

  const sortedSections = useMemo(() => {
    if (!sortField) return sections;

    return [...sections].sort((a, b) => {
      let aVal: string | boolean = '';
      let bVal: string | boolean = '';

      switch (sortField) {
        case 'category':
          aVal = getCategory(a.sectionKey);
          bVal = getCategory(b.sectionKey);
          break;
        case 'section':
          aVal = a.sectionKey;
          bVal = b.sectionKey;
          break;
        case 'label':
          aVal = getDisplayLabel(a);
          bVal = getDisplayLabel(b);
          break;
        case 'visible':
          aVal = a.isVisible;
          bVal = b.isVisible;
          break;
        case 'active':
          aVal = a.isActive;
          bVal = b.isActive;
          break;
      }

      if (typeof aVal === 'boolean') {
        const result = aVal === bVal ? 0 : aVal ? -1 : 1;
        return sortDir === 'asc' ? result : -result;
      }

      const result = (aVal as string).localeCompare(bVal as string);
      return sortDir === 'asc' ? result : -result;
    });
  }, [sections, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const handleToggleVisibility = async (sectionKey: string, currentVisible: boolean) => {
    try {
      const section = sections.find((s) => s.sectionKey === sectionKey);
      if (!section) return;

      if (section.id.startsWith('temp-')) {
        await createSectionVisibility({ sectionKey, isVisible: !currentVisible, isActive: true });
      } else {
        await updateSectionVisibility({ sectionKey, isVisible: !currentVisible });
      }

      toast.success(`Section ${!currentVisible ? 'shown' : 'hidden'} successfully`);
      await loadSections();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update section visibility');
    }
  };

  const handleToggleActive = async (sectionKey: string, currentActive: boolean) => {
    try {
      const section = sections.find((s) => s.sectionKey === sectionKey);
      if (!section) return;

      const newActive = !currentActive;
      const updates: { sectionKey: string; isActive: boolean; isVisible?: boolean } = {
        sectionKey,
        isActive: newActive
      };
      if (newActive) updates.isVisible = true;

      if (section.id.startsWith('temp-')) {
        await createSectionVisibility({
          sectionKey,
          isActive: newActive,
          isVisible: newActive ? true : section.isVisible
        });
      } else {
        await updateSectionVisibility(updates);
      }

      toast.success(`Section ${newActive ? 'activated' : 'deactivated'} successfully`);
      await loadSections();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update section status');
    }
  };

  const handleLevelsChange = async (sectionKey: string, levels: string[]) => {
    try {
      const section = sections.find((s) => s.sectionKey === sectionKey);
      if (!section) return;

      if (section.id.startsWith('temp-')) {
        await createSectionVisibility({
          sectionKey,
          isVisible: section.isVisible,
          isActive: section.isActive,
          visibleAtLevels: levels
        });
      } else {
        await updateSectionVisibility({ sectionKey, visibleAtLevels: levels });
      }

      toast.success('Section level visibility updated');
      await loadSections();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update level visibility');
    }
  };

  const startEditing = (sectionKey: string, currentLabel: string | null) => {
    setEditingKey(sectionKey);
    const defaultSection = DEFAULT_SECTIONS.find((s) => s.key === sectionKey);
    setEditLabel(currentLabel || defaultSection?.label || '');
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditLabel('');
  };

  const saveLabel = async (sectionKey: string) => {
    try {
      const section = sections.find((s) => s.sectionKey === sectionKey);
      if (!section) return;

      const defaultSection = DEFAULT_SECTIONS.find((s) => s.key === sectionKey);
      const customLabel = editLabel === defaultSection?.label ? null : editLabel;

      if (section.id.startsWith('temp-')) {
        await createSectionVisibility({
          sectionKey,
          customLabel: customLabel || undefined,
          isVisible: true,
          isActive: true
        });
      } else {
        await updateSectionVisibility({
          sectionKey,
          customLabel: customLabel || undefined
        });
      }

      toast.success('Section label updated successfully');
      setEditingKey(null);
      setEditLabel('');
      await loadSections();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update section label');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full">
      {/* Column Headers */}
      <div className="bg-sidebar-hover border-b border-border">
        <div className="px-6 py-3 grid grid-cols-12 gap-4 text-xs font-medium uppercase tracking-wider text-content-text-muted select-none">
          <div
            className="col-span-2 flex items-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('category')}
          >
            Category <SortIcon field="category" />
          </div>
          <div
            className="col-span-2 flex items-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('section')}
          >
            Section <SortIcon field="section" />
          </div>
          <div
            className="col-span-3 flex items-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('label')}
          >
            Label <SortIcon field="label" />
          </div>
          <div className="col-span-3 flex items-center text-content-text-muted">
            Levels
          </div>
          <div
            className="col-span-1 flex items-center justify-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('visible')}
          >
            Visible <SortIcon field="visible" />
          </div>
          <div
            className="col-span-1 flex items-center justify-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('active')}
          >
            Active <SortIcon field="active" />
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="w-full">
        {sortedSections.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg font-medium">No sections found</p>
          </div>
        )}

        {sortedSections.map((section) => (
          <div
            key={section.sectionKey}
            className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-sidebar-hover transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700"
          >
            <div className="col-span-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {getCategory(section.sectionKey)}
              </span>
            </div>
            <div className="col-span-2">
              <span className="font-mono text-sm text-gray-600 dark:text-gray-300">
                {section.sectionKey}
              </span>
            </div>
            <div className="col-span-3">
              {editingKey === section.sectionKey ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    className="h-8"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => saveLabel(section.sectionKey)}
                    className="h-8 w-8 p-0"
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={cancelEditing}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <span className="text-sm text-gray-900 dark:text-white">{getDisplayLabel(section)}</span>
                  {section.customLabel && (
                    <span className="text-xs text-gray-500">(custom)</span>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => startEditing(section.sectionKey, section.customLabel)}
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Edit2 className="h-3.5 w-3.5 text-gray-400" />
                  </Button>
                </div>
              )}
            </div>
            <div className="col-span-3 flex items-center">
              <LevelsSelector
                value={section.visibleAtLevels ?? []}
                onChange={(levels) => handleLevelsChange(section.sectionKey, levels)}
                disabled={!section.isActive}
              />
            </div>
            <div className="col-span-1 flex justify-center">
              <Switch
                checked={section.isVisible}
                disabled={!section.isActive}
                onCheckedChange={() =>
                  handleToggleVisibility(section.sectionKey, section.isVisible)
                }
              />
            </div>
            <div className="col-span-1 flex justify-center">
              <Switch
                checked={section.isActive}
                onCheckedChange={() =>
                  handleToggleActive(section.sectionKey, section.isActive)
                }
              />
            </div>
          </div>
        ))}
      </div>

      {/* How it works info */}
      <div className="px-6 py-6">
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">How it works:</h4>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>
              <strong>Active OFF:</strong> Section is completely hidden and inaccessible to ALL users, including admins. The section is decommissioned.
              <br />
              <span className="text-xs">Use this for sections that are no longer in use or maintained. Active takes precedence over Visible.</span>
            </li>
            <li>
              <strong>Active ON, Visible OFF:</strong> Section is hidden from regular users but accessible to admins.
              <br />
              <span className="text-xs">Use this for sections in development or under review — admins can still access and test them.</span>
            </li>
            <li>
              <strong>Active ON, Visible ON:</strong> Section is accessible to everyone.
              <br />
              <span className="text-xs">This is the normal state for live sections.</span>
            </li>
            <li>
              <strong>Label:</strong> Hover over a label and click the edit icon to rename. Clear the text to revert to default.
              <br />
              <span className="text-xs">Custom labels appear throughout the platform.</span>
            </li>
            <li>
              <strong>Levels:</strong> Restrict a section to specific location hierarchy levels — Country, Province, City, Neighbourhood — or the Organization portal context.
              <br />
              <span className="text-xs">Leave as "All levels" to show the section everywhere. Example: set "Organization only" for Directory sections, or "City, Neighbourhood, Organization" for community sections like Buy &amp; Sell.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SectionVisibilityTab;



